import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {useMemo} from 'react';

import {useEntityPageUserStatsQuery} from '../../generated/graphql';
import {useCachedData} from '../../util/cache';
import {propagateErrorsContext} from '../../util/errors';

type EntityStats = {
  id: string;
  username: string;
  name: string;
  photoUrl?: string | null;
  accountType?: string | null;
  runCount: number;
  showFallbackAvatar: boolean;
};

type UseEntityUserStatsHook =
  | {loading: true; data: null}
  | {
      loading: false;
      data: EntityStats[];
    };

/**
 * Retrieve a list of users and how many runs they have logged in the last week.
 *
 * This is used to show the list of users on the project page. This query is
 * quite expensive, so we use client-side caching so we can show outdated info
 * while we wait for the new data to load.
 *
 * This hook is passed a set of user IDs that have failed to load their avatars,
 * which is not strictly related to the function of this hook, but is important
 * for maintaining some backward compatibility with this logic.
 *
 * @param entityName
 * @param avatarFailedToLoadSet
 */
export function useEntityUserStats(
  entityName: string,
  avatarFailedToLoadSet: Set<string>
): UseEntityUserStatsHook {
  const userStatsStartDate = new Date();
  userStatsStartDate.setDate(userStatsStartDate.getDate() - 7);
  userStatsStartDate.setHours(0, 0, 0, 0);

  const entityPageUserStatsQuery = useEntityPageUserStatsQuery({
    variables: {
      entityName,
      userStatsStartDate: userStatsStartDate.toISOString(),
    },
    context: propagateErrorsContext(),
  });

  const result = useCachedData({
    namespace: 'entityUserStats',
    key: entityName,
    dataFromServer: entityPageUserStatsQuery.data,
    waitingForDataFromServer: entityPageUserStatsQuery.loading,
  });

  const data = useMemo(() => {
    if (!result) {
      return null;
    }
    const userStatEdges = result.entity?.userStats?.edges;

    if (!userStatEdges) {
      return null;
    }

    return userStatEdges
      .map(e => {
        if (!e.node || !e.node.user) {
          return null;
        }
        const user = e.node.user;

        return {
          id: user.id,
          username: user.username!,
          name: user.name,
          photoUrl: user.photoUrl,
          accountType: user.accountType,
          runCount: e.node.runCount,
          showFallbackAvatar:
            !e.node.user.photoUrl || avatarFailedToLoadSet.has(e.node.user.id),
        };
      })
      .filter(isNotNullOrUndefined);
  }, [avatarFailedToLoadSet, result]);

  // This is somewhat more verbose than it might otherwise be, because
  // TypeScript didn't want to infer the discriminated union.
  if (data == null) {
    return {loading: true, data: null};
  } else {
    return {loading: false, data};
  }
}
